import apiClient from './apiClient';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  interval: 'monthly' | 'yearly';
  features: string[];
  isActive: boolean;
  productId: string;
  priceId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionPlansResponse {
  data: SubscriptionPlan[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export interface CreatePlanData {
  name: string;
  description?: string;
  price: number;
  currency?: string;
  interval: 'monthly' | 'yearly';
  features?: string[];
  isActive?: boolean;
}

export interface UpdatePlanData {
  description?: string;
  features?: string[];
  isActive?: boolean;
}

export const subscriptionPlanService = {
  /**
   * Get admin subscription plans with pagination and filtering
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @param status - Filter by status: 'all', 'active', 'inactive'
   * @returns Paginated subscription plans
   */
  getPlans: async (
    page: number = 1,
    limit: number = 10,
    status: 'all' | 'active' | 'inactive' = 'all'
  ): Promise<SubscriptionPlansResponse> => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (status !== 'all') {
        params.append('status', status);
      }

      const response = await apiClient.get<SubscriptionPlansResponse>(
        `/admin/subscription-plans?${params.toString()}`
      );

      console.log('Fetched subscription plans:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw error;
    }
  },

  /**
   * Create a new subscription plan
   * @param data - Plan data
   * @returns Created plan
   */
  createPlan: async (data: CreatePlanData): Promise<SubscriptionPlan> => {
    try {
      console.log('Creating subscription plan:', data);
      const response = await apiClient.post<SubscriptionPlan>(
        '/admin/subscription-plans',
        data
      );

      console.log('Plan created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      throw error;
    }
  },

  /**
   * Update an existing subscription plan
   * @param id - Plan ID
   * @param data - Updated plan data
   * @returns Updated plan
   */
  updatePlan: async (id: string, data: UpdatePlanData): Promise<SubscriptionPlan> => {
    try {
      console.log('Updating subscription plan:', id, data);
      const response = await apiClient.patch<SubscriptionPlan>(
        `/admin/subscription-plans/${id}`,
        data
      );

      console.log('Plan updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      throw error;
    }
  },

  /**
   * Toggle plan active/inactive status
   * @param id - Plan ID
   * @param isActive - New active status
   * @returns Updated plan
   */
  togglePlanStatus: async (id: string, isActive: boolean): Promise<SubscriptionPlan> => {
    try {
      console.log('Toggling plan status:', id, isActive);
      const response = await apiClient.patch<SubscriptionPlan>(
        `/admin/subscription-plans/${id}`,
        { isActive }
      );

      console.log('Plan status toggled successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error toggling plan status:', error);
      throw error;
    }
  },
};
